import { useAnimals, useLots, useProperties, useRegisterMovement } from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { todayIso } from '@/lib/format';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeftRight, CircleDollarSign, Skull } from 'lucide-react';
import { useEffect, useState } from 'react';

const MOVEMENT_TYPES: {
  value: string;
  label: string;
  shortLabel: string;
  endpoint: string;
  icon: LucideIcon;
}[] = [
  {
    value: 'transfer',
    label: 'Transferência',
    shortLabel: 'Transferência',
    endpoint: 'transfer',
    icon: ArrowLeftRight,
  },
  {
    value: 'sale',
    label: 'Venda',
    shortLabel: 'Venda',
    endpoint: 'sale',
    icon: CircleDollarSign,
  },
  {
    value: 'death',
    label: 'Morte',
    shortLabel: 'Morte',
    endpoint: 'death',
    icon: Skull,
  },
];

type TransferMode = 'same_property' | 'other_property' | 'external';
type MovementScope = 'animal' | 'lot';

function buildFormState(presetAnimalId?: number, presetLotId?: number) {
  return {
    movement_date: todayIso(),
    notes: '',
    animal_ids: presetAnimalId ? [presetAnimalId] : ([] as number[]),
    lot_id: presetLotId != null ? String(presetLotId) : '',
    transfer_mode: 'same_property' as TransferMode,
    destination_property_id: '',
    destination_lot_id: '',
    off_property_notes: '',
    sale_value: '',
    sale_date: todayIso(),
  };
}

export function MovementFormModal({
  open,
  onClose,
  propertyId,
  presetAnimalId,
  presetLotId,
  presetLotName,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  presetAnimalId?: number;
  presetLotId?: number;
  presetLotName?: string;
}) {
  const lockedScope: MovementScope | null =
    presetLotId != null ? 'lot' : presetAnimalId != null ? 'animal' : null;

  const [scope, setScope] = useState<MovementScope>(lockedScope ?? 'animal');
  const [type, setType] = useState('transfer');
  const [error, setError] = useState('');
  const [form, setForm] = useState(() => buildFormState(presetAnimalId, presetLotId));

  const isLotScope = scope === 'lot';
  const lotIdNum = form.lot_id ? Number(form.lot_id) : null;

  const { data: properties } = useProperties();
  const { data: lots } = useLots(propertyId);
  const destPropertyId =
    form.transfer_mode === 'other_property' && form.destination_property_id
      ? Number(form.destination_property_id)
      : form.transfer_mode === 'same_property'
        ? propertyId
        : null;
  const { data: destLots } = useLots(destPropertyId);
  const { data: animals } = useAnimals({
    property_id: propertyId,
    lot_id: isLotScope && lotIdNum ? lotIdNum : undefined,
    per_page: isLotScope ? 500 : 100,
  });

  const config = MOVEMENT_TYPES.find((t) => t.value === type)!;
  const register = useRegisterMovement(config.endpoint);

  useEffect(() => {
    if (!open) return;
    setType('transfer');
    setError('');
    setScope(lockedScope ?? 'animal');
    setForm(buildFormState(presetAnimalId, presetLotId));
  }, [open, presetAnimalId, presetLotId, lockedScope]);

  function switchScope(next: MovementScope) {
    if (lockedScope) return;
    setScope(next);
    setForm((f) => ({
      ...f,
      animal_ids: next === 'animal' ? f.animal_ids : [],
      lot_id: next === 'lot' ? f.lot_id : '',
    }));
  }

  function toggleAnimal(id: number) {
    setForm((f) => ({
      ...f,
      animal_ids: f.animal_ids.includes(id)
        ? f.animal_ids.filter((x) => x !== id)
        : [...f.animal_ids, id],
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (isLotScope && !lotIdNum) {
      setError('Selecione um lote.');
      return;
    }
    if (!isLotScope && form.animal_ids.length === 0) {
      setError('Selecione ao menos um animal.');
      return;
    }

    const body: Record<string, unknown> = {
      property_id: propertyId,
      movement_date: form.movement_date,
      scope: isLotScope ? 'lot' : 'animal',
      notes: form.notes || null,
    };

    if (isLotScope) {
      body.lot_id = lotIdNum;
    } else {
      body.animal_ids = form.animal_ids;
    }

    if (type === 'transfer') {
      if (form.transfer_mode === 'external') {
        if (!form.off_property_notes.trim()) {
          setError('Informe o destino (fazenda externa ou outro local).');
          return;
        }
        body.is_off_property = true;
        body.off_property_notes = form.off_property_notes.trim();
      } else {
        if (!form.destination_lot_id) {
          setError('Selecione o lote de destino.');
          return;
        }
        if (form.transfer_mode === 'other_property' && !form.destination_property_id) {
          setError('Selecione a fazenda de destino.');
          return;
        }
        body.destination_lot_id = Number(form.destination_lot_id);
        body.is_off_property = false;
      }
    }
    if (type === 'sale') {
      body.sale_date = form.sale_date;
      body.sale_value = form.sale_value ? Number(form.sale_value) : null;
    }

    try {
      await register.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao registrar.'));
    }
  }

  const sourceLotId = isLotScope ? lotIdNum : null;
  const destinationLotOptions = (destLots?.lots ?? [])
    .filter((l) => l.is_active && l.id !== sourceLotId)
    .map((l) => ({ value: l.id, label: l.name }));

  const otherPropertyOptions = (properties?.properties ?? [])
    .filter((p) => p.id !== propertyId)
    .map((p) => ({ value: p.id, label: p.name }));

  const sourceLotOptions = (lots?.lots ?? [])
    .filter((l) => l.is_active)
    .map((l) => ({ value: l.id, label: l.name }));

  const title =
    lockedScope === 'lot'
      ? `Movimentação — ${presetLotName ?? 'lote'}`
      : 'Nova movimentação';

  const animalCount = isLotScope ? (animals?.meta.total ?? animals?.animals.length ?? 0) : 0;
  const selectedLotName =
    presetLotName ?? (lots?.lots ?? []).find((l) => l.id === lotIdNum)?.name;

  const canSubmit = isLotScope ? !!lotIdNum : form.animal_ids.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="movement-form-modal"
            loading={register.isPending}
            disabled={!canSubmit}
          >
            Registrar
          </Button>
        </>
      }
    >
      <form id="movement-form-modal" onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!lockedScope && (
          <div>
            <p className="mb-2 text-sm font-medium text-manejai-bark">Registrar por</p>
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-manejai-sand bg-manejai-cream/30 p-1">
              <button
                type="button"
                onClick={() => switchScope('animal')}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                  scope === 'animal'
                    ? 'bg-white text-manejai-forest shadow-sm'
                    : 'text-manejai-bark/70 hover:text-manejai-forest'
                }`}
              >
                Animal(is)
              </button>
              <button
                type="button"
                onClick={() => switchScope('lot')}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                  scope === 'lot'
                    ? 'bg-white text-manejai-forest shadow-sm'
                    : 'text-manejai-bark/70 hover:text-manejai-forest'
                }`}
              >
                Lote inteiro
              </button>
            </div>
            <p className="mt-1.5 text-xs text-manejai-bark/60">
              Escolha uma opção: ou animais individuais, ou um lote — não os dois.
            </p>
          </div>
        )}

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-manejai-forest">Tipo</legend>
          <div className="grid grid-cols-3 gap-2">
            {MOVEMENT_TYPES.map((movementType) => {
              const Icon = movementType.icon;
              const selected = type === movementType.value;
              return (
                <button
                  key={movementType.value}
                  type="button"
                  title={movementType.label}
                  aria-pressed={selected}
                  onClick={() => setType(movementType.value)}
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-2xl border px-2 py-3 text-center transition-colors',
                    selected
                      ? 'border-manejai-forest bg-manejai-forest/5 text-manejai-forest shadow-sm'
                      : 'border-manejai-sand bg-white text-manejai-bark/80 hover:border-manejai-sage hover:bg-manejai-cream/40',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-xl',
                      selected ? 'bg-manejai-forest text-white' : 'bg-manejai-sand/60 text-manejai-forest',
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <span className="text-xs font-medium leading-tight">{movementType.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <DateInput
          label="Data"
          required
          value={form.movement_date}
          onChange={(movement_date) => setForm({ ...form, movement_date })}
        />

        {type === 'transfer' && (
          <>
            <Select
              label="Destino da transferência"
              value={form.transfer_mode}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  transfer_mode: e.target.value as TransferMode,
                  destination_property_id: '',
                  destination_lot_id: '',
                  off_property_notes: '',
                }))
              }
              options={[
                { value: 'same_property', label: 'Outro lote nesta fazenda' },
                { value: 'other_property', label: 'Outra fazenda da minha conta' },
                { value: 'external', label: 'Fazenda externa / outro local' },
              ]}
            />
            {form.transfer_mode === 'other_property' && (
              <Select
                label="Fazenda de destino"
                required
                value={form.destination_property_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    destination_property_id: e.target.value,
                    destination_lot_id: '',
                  })
                }
                placeholder={
                  otherPropertyOptions.length === 0
                    ? 'Nenhuma outra fazenda na conta'
                    : 'Selecione a fazenda…'
                }
                options={otherPropertyOptions}
              />
            )}
            {(form.transfer_mode === 'same_property' ||
              (form.transfer_mode === 'other_property' && form.destination_property_id)) && (
              <Select
                label="Lote de destino"
                required
                value={form.destination_lot_id}
                onChange={(e) => setForm({ ...form, destination_lot_id: e.target.value })}
                placeholder={
                  destinationLotOptions.length === 0
                    ? 'Nenhum lote disponível'
                    : 'Selecione o lote…'
                }
                options={destinationLotOptions}
              />
            )}
            {form.transfer_mode === 'external' && (
              <Input
                label="Destino (fazenda externa ou outro local)"
                required
                placeholder="Ex.: Fazenda Santa Maria, arrendamento, frigorífico…"
                value={form.off_property_notes}
                onChange={(e) => setForm({ ...form, off_property_notes: e.target.value })}
              />
            )}
          </>
        )}

        {type === 'sale' && (
          <>
            <DateInput
              label="Data da venda"
              required
              value={form.sale_date}
              onChange={(sale_date) => setForm({ ...form, sale_date })}
            />
            <Input
              label="Valor da venda (R$)"
              type="number"
              step="0.01"
              value={form.sale_value}
              onChange={(e) => setForm({ ...form, sale_value: e.target.value })}
            />
          </>
        )}

        {isLotScope ? (
          <div className="space-y-3">
            {lockedScope === 'lot' ? (
              <p className="rounded-xl border border-manejai-sand bg-manejai-cream/40 px-4 py-3 text-sm text-manejai-bark/80">
                Todos os <strong>{animalCount}</strong> animal(is) ativos do lote{' '}
                <strong>{selectedLotName}</strong> serão incluídos nesta movimentação.
              </p>
            ) : (
              <>
                <Select
                  label="Lote"
                  required
                  value={form.lot_id}
                  onChange={(e) => setForm({ ...form, lot_id: e.target.value, animal_ids: [] })}
                  placeholder="Selecione o lote…"
                  options={sourceLotOptions}
                />
                {lotIdNum ? (
                  <p className="rounded-xl border border-manejai-sand bg-manejai-cream/40 px-4 py-3 text-sm text-manejai-bark/80">
                    Todos os <strong>{animalCount}</strong> animal(is) ativos do lote{' '}
                    <strong>{selectedLotName}</strong> serão incluídos.
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-2 text-sm font-medium text-manejai-bark">Animais</p>
            <div className="max-h-48 overflow-y-auto rounded-xl border border-manejai-sand divide-y divide-manejai-sand/60">
              {(animals?.animals ?? []).map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-manejai-cream/50"
                >
                  <input
                    type="checkbox"
                    checked={form.animal_ids.includes(a.id)}
                    onChange={() => toggleAnimal(a.id)}
                    className="rounded border-manejai-sand text-manejai-forest focus:ring-manejai-sage"
                  />
                  {a.internal_number} {a.animal_name && `— ${a.animal_name}`}
                  {a.lot_name && (
                    <span className="ml-auto text-xs text-manejai-bark/50">{a.lot_name}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        <Input
          label="Observações"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </form>
    </Modal>
  );
}
